import axios from "axios";
import { adminApi } from "../../lib/axios";
export interface CampaignStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalSales: number;
  averageSalesPerCampaign: number;
}
export interface AdminCampaign {
  id: string;
  title: string;
  code: string;
  createdAt: string;
  status: string;
  brandId: string;
  brandName: string;
  brandWebsite: string;
  shopDomain?: string;
  totalSales: number;
  salesCount: number;
  link: string;
  targetAllProducts: 'All products' | 'Selected products';
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  discountValue: number;
  minimumPurchaseAmount: number | null;
  usageLimit: string;
  minimumRequirement: string;
  appliesOnEachItem: boolean;
  tags: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SearchAdminCampaignsResponse {
  data: AdminCampaign[];
  pagination: Pagination;
}

// Search parameters interface
export interface CampaignSearchParams {
  page?: number;
  limit?: number;
  keyword?: string;
  sortOrder?: "ASC" | "DESC";
  status?: "ACTIVE" | "PAUSED";
  brandId?: string;
}

// Service class
export class CampaignBrandsService {
  // Search brands with advanced filtering and pagination
  async searchCampaigns(
    params: CampaignSearchParams = {}
  ): Promise<SearchAdminCampaignsResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      // Add search parameters
      if (params.keyword) queryParams.append("keyword", params.keyword);
      if (params.brandId) queryParams.append("brandId", params.brandId);
      if (params.status) queryParams.append("status", params.status);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await adminApi.get(
        `/product-campaign/admin/campaigns?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search campaigns"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getFilteringBrands(): Promise<{id:string, name:string}[]> {
    const response = await adminApi.get("brand/admin/minimal");
    return response.data;
  }

  formatCampaignForDisplay(campaign: AdminCampaign) {

    return {
      id: campaign.id,
      title: campaign.title,
      brandId: campaign.brandId,
      brandName: campaign.brandName,
      brandWebsite: campaign.brandWebsite,
      totalSales: campaign.totalSales,
      salesCount: campaign.salesCount,
      status: campaign.status,
      createdAt: campaign.createdAt,
      link: campaign.link,
      targetAllProducts: campaign.targetAllProducts,
      discountType: campaign.discountType,
      discountValue: campaign.discountValue,
      minimumPurchaseAmount: campaign.minimumPurchaseAmount,
      usageLimit: campaign.usageLimit,
      minimumRequirement: campaign.minimumRequirement,
      appliesOnEachItem: campaign.appliesOnEachItem,
      tags: campaign.tags
    };
  }

  async getCampaignStats(): Promise<CampaignStats> {
    try {
      const response = await adminApi.get(
        "/product-campaign/admin/overview"
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch campaign stats:", error);
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalSales: 0,
        averageSalesPerCampaign: 0,
      };
    }
  }
}

// Export singleton instance
export const campaignBrandsService = new CampaignBrandsService();
